import { gql } from '@apollo/client';

export const GET_SPARKS_GRADIENTS = gql`
  query getSparksGradients {
    allColors(filters: { colorType: PREDEFINED_GRADIENT }) {
      totalCount
      items {
        id
        colorId
        colorValue
        predefinedGradientName
      }
    }
  }
`;

export const CREATE_SPARK = gql`
  mutation createSpark(
    $bgColorId: String
    $title: String!
    $isPrivate: Boolean
    $coverMediaId: String
    $categorySlug: String
    $description: String
  ) {
    createSpark(
      bgColorId: $bgColorId
      title: $title
      isPrivate: $isPrivate
      coverMediaId: $coverMediaId
      categorySlug: $categorySlug
      description: $description
    ) {
      id
      cardId
      type
      title
      description
      isPrivateList
      categorySlug
      cardStyle {
        id
        bgColor {
          id
          colorId
          colorValue
          predefinedGradientName
        }
      }
      userCoverSelection {
        id
        mediaUrl
        mediaType
        thumbnailUrl
        detailMediaUrl
        detailMediaType
        detailThumbnailUrl
      }
    }
  }
`;

export const UPDATE_SPARK = gql`
  mutation updateSpark($sparkId: String!, $title: String, $bgColorId: String) {
    updateSpark(sparkId: $sparkId, title: $title, bgColorId: $bgColorId) {
      id
      cardId
      type
      title
      cardStyle {
        id
        bgColor {
          id
          colorId
          colorValue
          predefinedGradientName
        }
      }
    }
  }
`;

export const GET_USER_SPARKS = gql`
  query getUserSparks($id: String!, $limit: Int = 10, $offset: Int) {
    allUsers(filters: { id: $id }) {
      totalCount
      items {
        id
        username
        cardId
        pickedCards(limit: $limit, offset: $offset, filters: { type: SPARK }) {
          totalCount
          items {
            id
            cardId
            title
            description
            type
            createdAt
            categorySlug
            cardStyle {
              id
              bgStyle
              bgColor {
                id
                colorValue
                predefinedGradientName
              }
            }
            userCoverSelection {
              id
              mediaUrl
              mediaType
              thumbnailUrl
              detailMediaUrl
              detailMediaType
              detailThumbnailUrl
            }
            extraData(limit: 1) {
              ...ExtraDataFields
            }
            sparkResponses(limit: 4) {
              totalCount
              items {
                id
                cardId
                type
                linkImageSets(limit: 1) {
                  totalCount
                  items {
                    id
                    images(filters: { imageType: PREVIEW }) {
                      url
                    }
                  }
                }
              }
            }
            comments(limit: 1) {
              totalCount
              items {
                id
                text
                user {
                  ...PreviewActionUser
                }
              }
            }
            user {
              ...PreviewActionUser
            }
            usersWhoAnswered {
              totalCount
              items {
                ...PreviewActionUser
              }
            }
          }
        }
      }
    }
  }
`;

export const SPARK_FIELDS = gql`
  fragment SparkFields on CardSchema {
    id
    cardId
    type
    title
    description
    createdAt
    categorySlug
    user {
      ...PreviewActionUser
      cardId
    }
    cardStyle {
      id
      layout
      bgStyle
      bgColor {
        id
        colorId
        colorValue
        predefinedGradientName
        gradientStart
        gradientEnd
      }
    }
    userCoverSelection {
      id
      mediaUrl
      mediaType
      thumbnailUrl
      gradientStart
      gradientEnd
      detailMediaUrl
      detailMediaType
      detailThumbnailUrl
    }
  }
`;

export const GET_SPARK_DETAIL = gql`
  query getSparkDetail($sparkId: String) {
    allCards(filters: { id: $sparkId }) {
      totalCount
      items {
        ...SparkFields
      }
    }
  }
`;

export const GET_SPARK_DETAIL_CARDS = gql`
  query getSparkDetailCards($sparkId: String, $sparkCardId: String, $offset: Int, $refListId: String, $limit: Int) {
    allCards(filters: { id: $sparkId }) {
      totalCount
      items {
        id
        cardId
        type
        title
        description
        sparkResponses(limit: $limit, offset: $offset) {
          totalCount
          items {
            ...PreviewCard
            ...PreviewImages
            description
            user {
              ...PreviewActionUser
            }
            linkImageSets(limit: 1) {
              totalCount
              items {
                id
                imageId
                images(filters: { imageType: PREVIEW }) {
                  url
                  id
                }
              }
            }
            responseToSpark {
              id
              type
              title
              description
              cardId
            }
            extraData(referrerPickId: $sparkId) {
              ...ExtraDataFields
            }
            pickedByUsers(filters: { addedToSpark: $sparkCardId }) {
              ...PickedByUsersFields
            }
          }
        }
      }
    }
  }
`;

export const ADD_ANSWER_TO_SPARK = gql`
  mutation addCardToSpark($sparkId: String!, $pickId: String!, $refListId: String) {
    addCardToSpark(sparkId: $sparkId, pickId: $pickId) {
      id
      cardId
      type
      sparkResponses(filters: { id: $pickId }) {
        totalCount
        items {
          ...PreviewCard
          ...PreviewImages
          responseToSpark {
            id
            type
            title
            description
            cardId
          }
          extraData(referrerPickId: $sparkId) {
            ...ExtraDataFields
          }
          pickedByUsers(filters: { addedToSpark: $sparkId }) {
            ...PickedByUsersFields
          }
        }
      }
    }
  }
`;

export const DELETE_SPARK = gql`
  mutation deleteSpark($sparkId: String!) {
    deleteItem(cardId: $sparkId) {
      statusMessage
    }
  }
`;

export const SPARK_CARDS_FIELDS = gql`
  fragment SparkCardsFields on CardSchema {
    id
    cardId
    type
    title
    description
    createdAt
    isFeatured
    categorySlug
    user {
      ...PreviewActionUser
      cardId
    }
    cardStyle {
      id
      layout
      bgStyle
      bgColor {
        id
        colorId
        colorValue
        predefinedGradientName
      }
    }
    userCoverSelection {
      id
      mediaUrl
      mediaType
      thumbnailUrl
      createdAt
      updatedAt
      userId
      detailMediaUrl
      detailMediaType
      detailThumbnailUrl
    }
    sparkResponses(limit: 4) {
      totalCount
      items {
        id
        cardId
        type
        linkImageSets(limit: 1) {
          totalCount
          items {
            images(filters: { imageType: PREVIEW }) {
              url
            }
          }
        }
      }
    }
    usersWhoAnswered(sorting: ANSWER_CREATED_AT_DESC) {
      totalCount
      items {
        id
        cardId
        username
        ...PreviewActionUser
        ...CoreUserInfoFields
      }
    }
  }
`;

export const GET_SPARK_CARDS = gql`
  query getSparkCards($limit: Int = 10, $offset: Int) {
    allCards(filters: { type: SPARK }, sorting: CARD_CREATED_AT_DESC, limit: $limit, offset: $offset) {
      totalCount
      items {
        ...SparkCardsFields
        extraData(limit: 1) {
          ...ExtraDataFields
        }
      }
    }
  }
`;

export const GET_FEATURED_SPARK_CARDS = gql`
  query getFeaturedSparkCards($limit: Int = 10, $offset: Int) {
    allCards(
      filters: { type: SPARK, isFeatured: true }
      sorting: FEATURED_POSITION_ASC
      limit: $limit
      offset: $offset
    ) {
      totalCount
      items {
        ...SparkCardsFields
        extraData(limit: 1) {
          ...ExtraDataFields
        }
      }
    }
  }
`;

export const GET_SPARK_CATEGORIES = gql`
  query getSparkCategories($limit: Int, $offset: Int) {
    allSparkCategories(limit: $limit, offset: $offset) {
      totalCount
      items {
        id
        name
        slug
        description
        position
        isActive
        coverMedia {
          id
          name
          mediaUrl
          mediaType
          position
          isActive
          thumbnailUrl
          gradientStart
          gradientEnd
        }
      }
    }
  }
`;
